import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import Card from 'assets/components/card/Card';
import ThemeCardRow from 'assets/components/card/themes/Theme.Card.Row';
import Icon from 'assets/components/icon/Icon';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import DocumentPreview from 'assets/components/other/DocumentPreview';
import Page from 'assets/components/page/Page';
import { getLocales } from 'assets/locales/Locale';
import BigHr from 'assets/templates/dividers/BigHr';
import Theme from 'assets/themes/Theme.Common';
import { formatFormPhone } from 'assets/utils/forms/formatFormPhone';
import { keys } from 'lodash';
import Client from 'models/claims/client/Model.Client';
import { ClaimCreatorOptions } from 'models/enums/ClaimCreation';
import ClaimContractSearchForm from 'module/claims/views/claim/other/Claim.ContractSearchForm';
import { PublicClaimSubmitProvider } from './Claim.Submit.Hooks';
import ClaimDocumentForm from './components/Claim.DocumentForm';
import { PublicClaimSubmitRepairFacilityForm } from './components/Claim.Submit.RepairFacility.Form';
import ClaimSubmitClaimJobDataPoints from './components/claimJob/Claim.Submit.ClaimJobDataPoints';
import ClaimSubmitClaimJobDocuments from './components/claimJob/Claim.Submit.ClaimJobDocuments';
import ClaimSubmitClaimJobInfo from './components/claimJob/Claim.Submit.ClaimJobInfo';
import ClaimSubmitClaimJobItems from './components/claimJob/claimJobItems/Claim.Submit.ClaimJobItems';
import ThemePageCentered from 'assets/components/page/themes/Theme.Page.Centered';
import Box from 'assets/components/box/Box';
import ThemeWrapperStack from 'assets/themes/wrappers/Theme.Wrapper.Stack';

export const publicClaimSubmitIndexRoute = '/public/claim/submit';
export default function PublicClaimSubmitIndex() {
  const { lang, translate } = getLocales();

  return (
    <PublicClaimSubmitProvider
      render={({
        claimTypesListPublicApi,
        dealerListPublicApi,
        repairFacilityListApi,
        uploadableDocuments,
        form,
        shouldShowClaimJobs,
        addNewClaimJob,
        deleteClaimJob,
      }) => {
        const resetedClaimData = {
          ...form.data.claim,
          dealerId: null,
          odometerReading: undefined,
          workOrderNumber: undefined,
          createdByPayee: {
            phoneNumber: undefined,
            email: undefined,
            name: undefined,
            payeeTypeId: undefined,
          },
          claimDocuments: [],
          claimJobs: form.data.claim?.claimJobs?.map((it) => ({
            ...it,
            claimJobItems: [],
            claimJobDocuments: [],
            dataPoints: [],
            claimType: undefined,
            claimTypeId: undefined,
            customerComplaint: undefined,
            correction: undefined,
            cause: undefined,
          })),
        };
        return (
          <Page
            class={ThemePageCentered}
            isLoading={
              claimTypesListPublicApi.isExecuting ||
              dealerListPublicApi.isExecuting ||
              repairFacilityListApi.isExecuting
            }
            header={<h2>{lang.submitANewClaim}</h2>}
            style={{
              page: {
                '--pageWidth': '1380px',
                maxWidth: '98%',
              } as React.CSSProperties,
            }}
            render={
              <>
                {form.data.claim?.contract || form.data.claim?.contractId ? (
                  <>
                    <p>{lang.searchContractEnteringVinAndContractNumberOrClientLastName}</p>
                    <FormWrapper>
                      <h2>
                        <Card
                          class={ThemeCardRow}
                          style={{ padding: 0 }}
                          title={
                            <>
                              {lang.contractNumber}: {form.data?.claim?.contract?.appNumber}
                            </>
                          }
                          subtitle={
                            <>
                              {lang.clientName}: {new Client(form.data?.claim?.contract?.client)?.displayInfo.title}
                            </>
                          }
                          info={
                            <>
                              {lang.vin}: {form.data?.claim?.contract?.vehicle?.vin}
                            </>
                          }
                        />
                      </h2>
                      <hr style={{ width: Theme.Size.fill }} />
                      <p
                        style={{
                          backgroundColor: Theme.Color.warning,
                          color: 'white',
                          padding: `${Theme.Size.M} ${Theme.Size.L}`,
                          fontWeight: 'bold',
                          marginTop: 0,
                        }}
                      >
                        {lang.publicClaimCreationWarning}
                      </p>
                      <br />
                      <SelectInput
                        name="claimCreationOption"
                        label={lang.whoIsSubmittingTheClaim}
                        data={keys(ClaimCreatorOptions).map((it) => ({
                          id: it,
                          title: translate(it).replace(/_/g, ' '),
                        }))}
                        value={form.data?.claimCreationOption}
                        error={form.errors?.claimCreationOption}
                        onChange={({ claimCreationOption }) => {
                          form.update({
                            claimCreationOption,
                            repairFacilityId: undefined,
                            claim: {
                              ...resetedClaimData,
                            },
                          });
                        }}
                      />
                      {ClaimCreatorOptions.REPAIR_FACILITY === form.data?.claimCreationOption && (
                        <>
                          {!form.data.newPayee && (
                            <SelectInput
                              name="repairFacilityId"
                              label={lang.repairFacility}
                              data={repairFacilityListApi.payload?.data?.map((it) => it.displayInfo)}
                              value={form.data?.repairFacilityId}
                              error={form.errors?.repairFacilityId}
                              onAdd={() => {
                                form.update({
                                  newPayee: {} as Model.IPayee,
                                });
                              }}
                              onChange={({ repairFacilityId }) => {
                                form.update({
                                  repairFacilityId,
                                  claim: {
                                    ...resetedClaimData,
                                  },
                                });
                              }}
                            />
                          )}
                          {!!form.data.newPayee && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              <div
                                style={{ display: 'flex', gap: '10px', justifyContent: 'center', alignItems: 'center' }}
                              >
                                <h2>
                                  {lang.create} {lang.repairFacility}
                                </h2>
                                <Button
                                  class={ThemeButtonCircle}
                                  render={lang.cancel}
                                  media={<Icon class="fas-ban" />}
                                  onClick={() => {
                                    form.update({
                                      newPayee: null,
                                    });
                                  }}
                                  htmlElementProps={{ title: lang.chooseExistingRepairFacility }}
                                />
                              </div>
                              <PublicClaimSubmitRepairFacilityForm form={form} />
                            </div>
                          )}
                        </>
                      )}
                      {ClaimCreatorOptions.DEALERSHIP === form.data?.claimCreationOption && (
                        <SelectInput
                          name="dealerId"
                          label={lang.dealership}
                          data={dealerListPublicApi.payload?.data?.map((it) => it.displayInfo)}
                          value={form.data?.claim?.dealerId}
                          error={form.errors?.claim?.dealerId}
                          onChange={({ dealerId }) =>
                            form.update({
                              repairFacilityId: undefined,
                              claim: {
                                ...resetedClaimData,
                                dealerId,
                              },
                            })
                          }
                        />
                      )}
                      {shouldShowClaimJobs && (
                        <>
                          {form.data.claim?.claimJobs?.map((job, idx) => (
                            <div
                              key={idx}
                              style={{ marginBottom: '2rem', borderBottom: '1px solid #ccc', paddingBottom: '1rem' }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h2 style={{ display: 'flex', alignItems: 'center' }}>
                                  {lang.details} #{idx + 1}
                                </h2>
                                <Button
                                  class={ThemeButtonCircle}
                                  media={<Icon class="fas-trash" />}
                                  render={lang.delete}
                                  data={idx}
                                  active
                                  disabled={form.data.claim?.claimJobs?.length === 1}
                                  onClick={deleteClaimJob}
                                />
                              </div>

                              <ClaimSubmitClaimJobInfo claimJob={job} index={idx} />
                              <ClaimSubmitClaimJobDocuments claimJob={job} index={idx} />
                              <ClaimSubmitClaimJobDataPoints claimJob={job} index={idx} />
                              <ClaimSubmitClaimJobItems claimJob={job} index={idx} />
                            </div>
                          ))}
                        </>
                      )}
                      {!form.data?.claim?.claimJobs?.some((job) =>
                        uploadableDocuments.some((doc) => doc.claimTypeId === job.claimTypeId)
                      ) && (
                        <>
                          <h2 style={{ display: 'flex', alignItems: 'center' }}>{lang.documents}</h2>
                          <ClaimDocumentForm
                            onSubmit={(docs) => form.update({ claimDocuments: [...form.data.claimDocuments, ...docs] })}
                          />
                          <DocumentPreview
                            documents={[
                              ...form.data.claimDocuments.map((it, docIdx) => ({
                                id: it.id,
                                url: it.mediaFile?.url,
                                name: it.name,
                                extension: it.mediaFile?.extension,
                                description: it.notes,
                                info: lang.new,
                                documentType: lang.document,
                                onDelete: () => {
                                  const docs = [...form.data.claimDocuments];
                                  const newDocs = docs.filter((_, tempIdx) => docIdx !== tempIdx);
                                  form.update({ claimDocuments: newDocs });
                                },
                              })),
                            ]}
                            isDeleteDisabled={false}
                          />
                          <br />
                        </>
                      )}
                      <BigHr />
                      <h2 style={{ display: 'flex', alignItems: 'center' }}>{lang.contactInfo}</h2>
                      <InputWrapper>
                        <TextInput
                          label={lang.contactName}
                          name="name"
                          value={form.data?.claim?.createdByPayee?.name}
                          error={form.errors?.claim?.createdByPayee?.name}
                          onChange={({ name }) => form.merge({ claim: { createdByPayee: { name: name } } })}
                        />
                        <TextInput
                          label={lang.phoneNumber}
                          name="phone"
                          value={form.data?.claim?.createdByPayee?.phoneNumber}
                          error={form.errors?.claim?.createdByPayee?.phoneNumber}
                          onChange={({ phone }) =>
                            form.merge({
                              claim: {
                                createdByPayee: {
                                  phoneNumber: formatFormPhone(form.data?.claim?.createdByPayee?.phoneNumber, phone),
                                },
                              },
                            })
                          }
                        />
                        <TextInput
                          label={lang.email}
                          name="email"
                          value={form.data?.claim?.createdByPayee?.email}
                          error={form.errors?.claim?.createdByPayee?.email}
                          onChange={({ email }) => form.merge({ claim: { createdByPayee: { email: email } } })}
                        />
                      </InputWrapper>
                      <br />
                      <h2 style={{ display: 'flex', alignItems: 'center' }}>{lang.info}</h2>
                      <NumberInput
                        name="odometerReading"
                        label={lang.odometerReading}
                        value={form.data?.claim?.odometerReading}
                        onChange={({ odometerReading }) => form.merge({ claim: { odometerReading } })}
                      />
                      <TextInput
                        name="workOrderNumber"
                        label={lang.workOrderNumber}
                        value={form.data?.claim?.workOrderNumber}
                        onChange={({ workOrderNumber }) => form.merge({ claim: { workOrderNumber } })}
                      />
                    </FormWrapper>
                  </>
                ) : (
                  <FormWrapper>
                    <ClaimContractSearchForm onSubmit={(contract) => form.merge({ claim: { contract } })} />
                  </FormWrapper>
                )}
              </>
            }
            footer={
              <Box class={ThemeWrapperStack} style={{ marginLeft: 'auto', width: 'fit-content' }}>
                <Button
                  onClick={addNewClaimJob}
                  media={<Icon class="fas-plus" />}
                  render={`${lang.addNew} ${lang.claimJob}`}
                  active
                  disabled={!form.data.claim?.contract}
                  isLoading={form.isSubmitted}
                  class={ThemeButtonCircle}
                  htmlElementProps={{ title: `${lang.addNew} ${lang.claimJob}` }}
                />
                <Button
                  onClick={form.submit}
                  media={<Icon class="fas-save" />}
                  render={
                    <>
                      {lang.finish}
                      {form.data.claimDocuments.length > 0 && (
                        <>
                          &nbsp;+&nbsp;{form.data.claimDocuments.length}
                          &nbsp;
                          <Icon class="fas-paperclip" />
                        </>
                      )}
                    </>
                  }
                  active
                  disabled={form.hasErrors || !form.isUpdated}
                  isLoading={form.isSubmitted}
                  class={ThemeButtonCircle}
                  htmlElementProps={{ title: lang.finish }}
                />
              </Box>
            }
          />
        );
      }}
    />
  );
}
